/**
 * PollPanel — displays poll context and results for a shared partial game.
 *
 * Shows a banner when it's the player's turn to vote, and a results
 * panel after voting with options to continue or return to library.
 */

import { useState, useEffect, useCallback } from 'react';
import type { PollResponse, PollVoteType } from '../../types/library';
import type { CardNumber } from '../../types/game';
import { getCardName } from '../../utils/cards';
import { BACKEND_URL } from '../../config';

interface PollPanelProps {
  pseudo: string;
  sharedGameId: number;
  isAuthor: boolean;
  partialPhase?: number;
  refreshKey?: unknown;
}

export default function PollPanel({
  pseudo,
  sharedGameId,
  isAuthor,
  partialPhase,
  refreshKey,
}: PollPanelProps) {
  const [responses, setResponses] = useState<PollResponse[]>([]);
  const [myVote, setMyVote] = useState<PollResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchResponses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${sharedGameId}/poll?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (res.ok) {
        const data = await res.json();
        setResponses(data.responses ?? []);
        const myResponse = data.responses?.find(
          (r: PollResponse) => r.pseudo.toLowerCase() === pseudo.toLowerCase(),
        );
        setMyVote(myResponse ?? null);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [pseudo, sharedGameId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses, refreshKey]);

  useEffect(() => {
    const id = setInterval(fetchResponses, 10_000);
    return () => clearInterval(id);
  }, [fetchResponses]);

  const hasVoted = myVote !== null;
  const canSeeResults = isAuthor || hasVoted;

  const votesByChoice = new Map<string, { count: number; sample: PollResponse; isMine: boolean }>();
  for (const r of responses) {
    const vt = r.vote_type ?? 'card';
    const key = vt === 'ecart'
      ? `${vt}:${r.vote_value ?? r.card_played}`
      : `${vt}:${r.card_played}`;
    const existing = votesByChoice.get(key);
    const isMine = r.pseudo.toLowerCase() === pseudo.toLowerCase();
    if (existing) {
      existing.count += 1;
      if (isMine) existing.isMine = true;
    } else {
      votesByChoice.set(key, { count: 1, sample: r, isMine });
    }
  }

  function voteLabel(r: PollResponse): string {
    const vt = r.vote_type ?? 'card';
    if (vt === 'bid') {
      const bid = r.card_played;
      return bid === 0 ? 'Passe' : bid === 1 ? 'Petite' : bid === 2 ? 'Garde' : bid === 3 ? 'Garde sans' : bid === 4 ? 'Garde contre' : String(bid);
    }
    if (vt === 'ecart') {
      if (r.vote_value) {
        const cards = r.vote_value.split(',').map(Number);
        return cards.map(c => getCardName(c as CardNumber)).join(', ');
      }
      return `Écart (${r.card_played} cartes)`;
    }
    return getCardName(r.card_played as CardNumber);
  }

  const voteTypeLabel = partialPhase === 1 ? 'enchère' : partialPhase === 2 ? 'écart' : 'carte';

  return (
    <div className="library-poll-panel">
      <div className="library-poll-header">
        <span className="library-poll-title">
          Sondage ({responses.length} vote{responses.length !== 1 ? 's' : ''})
        </span>
      </div>

      {hasVoted && (
        <div className="library-poll-voted">
          Vous avez voté : <strong>{voteLabel(myVote!)}</strong>
        </div>
      )}

      {!hasVoted && !isAuthor && (
        <div className="library-poll-waiting">
          Jouez votre {voteTypeLabel} sur le plateau pour voter
        </div>
      )}

      {isAuthor && responses.length === 0 && !loading && (
        <div className="library-poll-waiting">
          En attente des votes des destinataires
        </div>
      )}

      {canSeeResults && responses.length > 0 && (
        <div className="library-poll-results">
          <div className="library-poll-label">Résultats</div>
          {Array.from(votesByChoice.entries())
            .sort((a, b) => b[1].count - a[1].count)
            .map(([key, choice]) => {
              const { count, sample, isMine } = choice;
              return (
                <div key={key} className="library-poll-result-row">
                  <span className="library-poll-result-card">
                    {voteLabel(sample)}
                    {isMine && ' ✓'}
                  </span>
                  <div className="library-poll-result-bar-container">
                    <div
                      className="library-poll-result-bar"
                      style={{ width: `${(count / responses.length) * 100}%` }}
                    />
                  </div>
                  <span className="library-poll-result-count">
                    {count} ({Math.round((count / responses.length) * 100)}%)
                  </span>
                </div>
              );
            })}

          <div className="library-poll-voters">
            {responses.map(r => (
              <span key={r.pseudo} className="library-poll-voter">
                {r.pseudo}: {voteLabel(r)}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="library-comments-loading">Chargement…</div>}
    </div>
  );
}

// ============================================
// Poll Vote Banner — displayed on the game board
// ============================================

interface PollVoteBannerProps {
  isMyTurn: boolean;
  partialPhase?: number;
  hasVoted: boolean;
}

export function PollVoteBanner({ isMyTurn, partialPhase, hasVoted }: PollVoteBannerProps) {
  if (hasVoted) return null;

  const isCardPoll = partialPhase !== 1 && partialPhase !== 2;
  if (isCardPoll && !isMyTurn) return null;

  const message = partialPhase === 1
    ? isMyTurn
      ? 'Choisissez votre enchère pour voter'
      : 'En attente de votre tour aux enchères…'
    : partialPhase === 2
      ? isMyTurn
        ? 'Choisissez vos cartes d\'écart pour voter'
        : 'En attente de l\'écart du preneur…'
      : 'Jouez la carte de votre choix pour voter';

  return (
    <div className={`poll-vote-banner${isMyTurn ? ' poll-vote-banner--active' : ''}`}>
      <div className="poll-vote-banner-icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </div>
      <div className="poll-vote-banner-content">
        <strong>Sondage en cours</strong>
        <span>{message}</span>
      </div>
    </div>
  );
}

// ============================================
// Poll After-Vote Modal — shown after the user votes
// ============================================

interface PollAfterVoteModalProps {
  pseudo: string;
  sharedGameId: number;
  voteType: PollVoteType;
  voteLabel: string;
  responses: PollResponse[];
  onContinue: () => void;
  onBackToLibrary: () => void;
}

export function PollAfterVoteModal({
  pseudo,
  sharedGameId: _sharedGameId,
  voteType,
  voteLabel: myVoteLabel,
  responses,
  onContinue,
  onBackToLibrary,
}: PollAfterVoteModalProps) {
  void _sharedGameId;

  function formatVote(r: PollResponse): string {
    const vt = r.vote_type ?? 'card';
    if (vt === 'bid') {
      const bid = r.card_played;
      return bid === 0 ? 'Passe' : bid === 1 ? 'Petite' : bid === 2 ? 'Garde' : bid === 3 ? 'Garde sans' : bid === 4 ? 'Garde contre' : String(bid);
    }
    if (vt === 'ecart') {
      if (r.vote_value) {
        const cards = r.vote_value.split(',').map(Number);
        return cards.map(c => getCardName(c as CardNumber)).join(', ');
      }
      return `Écart`;
    }
    return getCardName(r.card_played as CardNumber);
  }

  const votesByChoice = new Map<string, { count: number; label: string; isMine: boolean }>();
  for (const r of responses) {
    const key = voteType === 'ecart' ? (r.vote_value ?? String(r.card_played)) : String(r.card_played);
    const existing = votesByChoice.get(key);
    const isMine = r.pseudo.toLowerCase() === pseudo.toLowerCase();
    if (existing) {
      existing.count++;
      if (isMine) existing.isMine = true;
    } else {
      votesByChoice.set(key, { count: 1, label: formatVote(r), isMine });
    }
  }

  const sortedChoices = Array.from(votesByChoice.values()).sort((a, b) => b.count - a.count);

  return (
    <div className="poll-aftervote-overlay">
      <div className="poll-aftervote-modal">
        <div className="poll-aftervote-header">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <h3>Merci pour votre vote !</h3>
        </div>

        <div className="poll-aftervote-myvote">
          Votre choix : <strong>{myVoteLabel}</strong>
        </div>

        <div className="poll-aftervote-results">
          <div className="poll-aftervote-results-title">
            Résultats ({responses.length} vote{responses.length !== 1 ? 's' : ''})
          </div>
          {sortedChoices.map((choice, i) => (
            <div key={i} className="poll-aftervote-row">
              <span className="poll-aftervote-choice">
                {choice.label}
                {choice.isMine && ' ✓'}
              </span>
              <div className="poll-aftervote-bar-bg">
                <div
                  className="poll-aftervote-bar"
                  style={{ width: `${(choice.count / responses.length) * 100}%` }}
                />
              </div>
              <span className="poll-aftervote-pct">
                {choice.count} ({Math.round((choice.count / responses.length) * 100)}%)
              </span>
            </div>
          ))}

          <div className="poll-aftervote-voters">
            {responses.map(r => (
              <span key={r.pseudo} className="poll-aftervote-voter">
                {r.pseudo}: {formatVote(r)}
              </span>
            ))}
          </div>
        </div>

        <div className="poll-aftervote-actions">
          <button className="poll-aftervote-btn continue" onClick={onContinue}>
            Continuer la partie
          </button>
          <button className="poll-aftervote-btn library" onClick={onBackToLibrary}>
            Retour à la bibliothèque
          </button>
        </div>
      </div>
    </div>
  );
}
